// NASBA / CPE compliance content for the online-event detail page.
//
// Most of these fields now come from the CMS `caira-detail` record (see
// `getEventCpeContentFromCms`). The values below stay as the fallback for
// events whose `caira_detail` is missing or only partly filled in, and for the
// two fields the CMS still does not carry (`fieldOfStudy`,
// `additionalCourseMaterial`, `howToEarnCpe`).

export interface EventCpeMeta {
  /** e.g. "2.0" — rendered as "2.0 CPE Credits" */
  credits: string;
  /** e.g. "120 mins" */
  sessionDuration: string;
  /** e.g. "Basic" */
  courseLevel: string;
  /** e.g. "5" */
  pollingQuestions: string;
  /** e.g. "Information Technology" */
  fieldOfStudy: string;
  prerequisiteEducation: string;
  advancedPreparation: string;
  courseMaterial: string;
  additionalCourseMaterial: string;
  deliveryMethod: string;
  /** Whether this event includes a Credly/CAIRA badge (shows a chip on the card). */
  badgeIncluded: boolean;
  /**
   * Whether the event card shows the CPE credit / badge chips. On for CAIRA
   * (NASBA) events, or any event whose CMS record sets `cpe_credits`.
   */
  showCpeChips: boolean;
  /** What a learner must do to earn CPE credit for the session. */
  howToEarnCpe: string[];
}

// Course brand logos shown on the event card chip row. Keyed by course name.
const COURSE_LOGOS: Record<string, string> = {
  CAIRA:
    "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/CAIRALogo.webp",
};

export const getCourseLogo = (courseName?: string): string | undefined =>
  courseName ? COURSE_LOGOS[courseName] : undefined;

export const NASBA_LOGO_URL =
  "https://ik.imagekit.io/mileseducation/miles_website/masterclass/nasba.webp";

export const NASBA_REGISTRY_URL = "https://www.nasbaregistry.org";

// Miles Masterclass Inc.'s NASBA National Registry sponsor id (static).
export const NASBA_SPONSOR_ID = "149174";

export const SUPPORT_EMAIL = "support@milesmasterclass.com";

export const NASBA_SPONSOR_STATEMENT =
  "Miles Masterclass Inc. is registered with the National Association of State Boards of Accountancy (NASBA) as a sponsor of continuing professional education on the National Registry of CPE Sponsors. State boards of accountancy have final authority on the acceptance of individual courses for CPE credit. Complaints regarding registered sponsors may be submitted to the National Registry of CPE Sponsors through its web site:";

export const REFUND_POLICY_STATEMENT =
  "For course refund policy, issue resolution, and additional info please refer to the FAQs on the Overview tab. For more information regarding administrative policies such as complaint and refund, please contact our offices at";

// Default NASBA/CPE metadata applied to every online webinar.
const DEFAULT_CPE_META: EventCpeMeta = {
  credits: "2.0",
  sessionDuration: "120 mins",
  courseLevel: "Basic",
  pollingQuestions: "5",
  fieldOfStudy: "Information Technology",
  prerequisiteEducation: "There are no prerequisites for this course",
  advancedPreparation:
    "There is no advance preparation required for this course",
  courseMaterial: "Material that will be presented during the session",
  additionalCourseMaterial:
    "Course material to include Glossary of important terms covered in the course",
  deliveryMethod: "Group Internet Based",
  badgeIncluded: true,
  showCpeChips: false,
  howToEarnCpe: [
    "Attend the live session for its full duration",
    "Answer the required number of polling questions",
    "Complete the post-session evaluation",
  ],
};

// Optional per-template overrides. Keyed by the Strapi `template` value.
const CPE_META_OVERRIDES: Record<string, Partial<EventCpeMeta>> = {
  // Only CAIRA webinars are NASBA CPE sessions, so only they show the chips by
  // default; CPA/CMA webinars stay chip-free unless the CMS sets `cpe_credits`.
  CAIRA: { showCpeChips: true },
};

export const getEventCpeContent = (template?: string): EventCpeMeta => {
  const override = template ? CPE_META_OVERRIDES[template] : undefined;
  return { ...DEFAULT_CPE_META, ...(override || {}) };
};

/** The CMS slices that carry CPE metadata for an event. */
export interface EventCpeSource {
  /** `caira_detail` relation on the Event (formerly `miles_one_detail`). */
  cairaDetail?: any;
  /** `sf_webinar_detail` relation on the Event. */
  sfWebinarDetail?: any;
}

// Treat empty strings / nulls as "not set in the CMS" so the boilerplate above
// still fills the gap.
const cmsText = (value: unknown): string | undefined =>
  value === null || value === undefined || value === ""
    ? undefined
    : String(value);

// `cpe_credits` is a decimal — render it the way the UI expects ("2.0").
const cmsCredits = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(1) : undefined;
};

/**
 * Build the CPE panel from the live API response, falling back to the
 * boilerplate for anything the CMS leaves blank.
 *
 * Sources: `caira-detail` (`cpe_credits`, `program_level`,
 * `no_question_answered`, `prerequisite_education`, `advance_preparation`,
 * `Course_material`, `int_delivery_method`, `badge_included`/`awards_cpe`) and
 * `sf-webinar-detail` (`duration`).
 */
export const getEventCpeContentFromCms = (
  source: EventCpeSource = {},
  template?: string,
): EventCpeMeta => {
  const base = getEventCpeContent(template);
  const { cairaDetail, sfWebinarDetail } = source;
  if (!cairaDetail && !sfWebinarDetail) return base;

  const credits = cmsCredits(cairaDetail?.cpe_credits);
  return {
    ...base,
    credits: credits ?? base.credits,
    showCpeChips: credits !== undefined || base.showCpeChips,
    sessionDuration: sfWebinarDetail?.duration
      ? `${sfWebinarDetail.duration} mins`
      : base.sessionDuration,
    courseLevel: cmsText(cairaDetail?.program_level) ?? base.courseLevel,
    pollingQuestions:
      cmsText(cairaDetail?.no_question_answered) ?? base.pollingQuestions,
    prerequisiteEducation:
      cmsText(cairaDetail?.prerequisite_education) ??
      base.prerequisiteEducation,
    advancedPreparation:
      cmsText(cairaDetail?.advance_preparation) ?? base.advancedPreparation,
    courseMaterial:
      cmsText(cairaDetail?.Course_material) ?? base.courseMaterial,
    deliveryMethod:
      cmsText(cairaDetail?.int_delivery_method) ?? base.deliveryMethod,
    // `badge_included` is the dedicated CMS toggle; fall back to `awards_cpe`
    // (legacy) then the static default.
    badgeIncluded:
      typeof cairaDetail?.badge_included === "boolean"
        ? cairaDetail.badge_included
        : typeof cairaDetail?.awards_cpe === "boolean"
          ? cairaDetail.awards_cpe
          : base.badgeIncluded,
  };
};

/** A single row in the detail-page NASBA panel. */
export interface NasbaRow {
  label: string;
  value: string;
  /** Optional pill shown after the value (e.g. "2 CPE" on Fields of Study). */
  chip?: string;
}

/**
 * Ordered rows for the NASBA panel, matching the approved layout.
 *
 * Rules:
 * - `Program Level`, `Prerequisite Education`, `Advanced Preparation` always
 *   render (with a sensible fallback when the CMS is blank).
 * - `Fields of Study`, `Sponsor Identification number` and
 *   `Additional Course Material Requirement` are static / boilerplate.
 * - Everything else is pulled straight from the CMS and **omitted entirely
 *   when the API value is null/blank** (no empty rows).
 */
export const getNasbaRows = (source: EventCpeSource = {}): NasbaRow[] => {
  const { cairaDetail: cd, sfWebinarDetail: sf } = source;
  const base = DEFAULT_CPE_META;
  const rows: NasbaRow[] = [];

  const credits = cmsCredits(cd?.cpe_credits);
  if (credits)
    rows.push({
      label: "Continuing Professional Education Credit (CPE)",
      value: credits,
    });

  rows.push({
    label: "Fields of Study",
    value: base.fieldOfStudy,
    chip: credits ? `${credits} CPE` : undefined,
  });

  rows.push({
    label: "Sponsor Identification number",
    value: NASBA_SPONSOR_ID,
  });

  const delivery = cmsText(cd?.int_delivery_method);
  if (delivery)
    rows.push({ label: "Instructional Delivery Method", value: delivery });

  rows.push({
    label: "Program Level",
    value: cmsText(cd?.program_level) ?? base.courseLevel,
  });
  rows.push({
    label: "Prerequisite Education",
    value: cmsText(cd?.prerequisite_education) ?? base.prerequisiteEducation,
  });
  rows.push({
    label: "Advanced Preparation",
    value: cmsText(cd?.advance_preparation) ?? base.advancedPreparation,
  });

  const duration = sf?.duration ? `${sf.duration} mins` : undefined;
  if (duration) rows.push({ label: "Session Duration", value: duration });

  const polling = cmsText(cd?.no_question_answered);
  if (polling) rows.push({ label: "No of polling questions", value: polling });

  const material = cmsText(cd?.Course_material);
  if (material) rows.push({ label: "Course Material", value: material });

  rows.push({
    label: "Additional Course Material Requirement",
    value: base.additionalCourseMaterial,
  });

  return rows;
};
